import { useEffect, useRef, useState } from 'react';
import { PanoramaViewer } from '@citizen-walk/shared-ui';
import type { PhotoProjection } from '@citizen-walk/shared-types';

/**
 * 写真表示 component (Sprint BG-DataSettings 16、GAP-U8 close)。
 *
 * **Note**: ファイル名は `PhotoView.tsx`。Windows の case-insensitive filesystem で
 * 既存 `photo.ts` (preprocessPhoto utility) と衝突するのを避けるため。
 * **export 名は `Photo`** (旧 v1 component 名と一致させる、外部からは `Photo` で参照)。
 *
 * 旧 `old/apps/user/src/components/Photo.tsx` (178 行) を踏襲 (L3):
 *   - 原寸 (src) が読めなかった場合、自動でサムネ (thumbSrc) に fallback
 *     → オフラインで原寸が SW キャッシュに無くても、サムネがあれば表示できる
 *   - 機内モード (navigator.onLine=false) では最初から thumbSrc 優先 (旧と同じ罠回避)
 *   - クリック / タップで全画面拡大モーダル (Lightbox) を開く
 *     → 拡大時もサムネで再表示できる (機内モード対応)
 *   - 両方失敗 → placeholder アイコン (📷) + テキスト
 *   - lazy load (loading="lazy") を default に
 *
 * v2 の追加点 (旧 v1 との差分):
 *   - inline style を撤去し Tailwind class のみ使用 (CLAUDE.md design system 原則、PostThumb と同方針)
 *   - placeholder UI を PostThumb と統一 (📷 アイコン + slate token)
 */
export interface PhotoProps {
  /** 原寸 (src)。null/undefined 可。 */
  src: string | null | undefined;
  /** サムネ (thumbSrc)。null/undefined 可、機内モード時の優先・原寸失敗時の fallback。 */
  thumbSrc: string | null | undefined;
  /** alt 属性。 */
  alt?: string;
  /** 追加 class (主に sizing/object-fit に使う)。 */
  className?: string;
  /** loading 属性 (default: 'lazy')。 */
  loading?: 'eager' | 'lazy';
  /** クリック / タップで拡大モーダルを開く (default true)。 */
  enableExpand?: boolean;
  /** 全 fallback が失敗したときに呼ばれる (任意)。 */
  onError?: () => void;
  /**
   * 写真の投影方式 (docs/27)。`equirectangular` なら原寸をパノラマビューアで見回し表示する
   * (サムネ fallback 中は平面表示 + 案内)。未指定 / `flat` は従来どおり。
   */
  projection?: PhotoProjection | null;
  /** 360度写真の画像中央の方位 (XMP PoseHeadingDegrees)。未指定は 0。 */
  baseHeadingDeg?: number | null;
  /** パノラマビューアの sizing class (既定: 高さ 18rem〜24rem)。 */
  panoramaClassName?: string;
}

export function Photo({
  src,
  thumbSrc,
  alt = '',
  className = '',
  loading = 'lazy',
  enableExpand = true,
  onError,
  projection = null,
  baseHeadingDeg = null,
  panoramaClassName = 'w-full h-72 sm:h-96 rounded-lg border border-slate-200',
}: PhotoProps) {
  // online 状態検知 (機内モード罠回避、旧 v1 L39-49 踏襲)。
  // 機内モードでは原寸 (src) は SW キャッシュに無いことが多く、<img> onError 経由 fallback は
  // 「失敗判定までの待ち時間」+「SW 経由レスポンスで error event が安定発火しない」二重問題で
  // 写真が表示されない事故が起きる → 機内モードでは最初から thumbSrc 優先で罠回避。
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  useEffect(() => {
    function on() {
      setIsOnline(true);
    }
    function off() {
      setIsOnline(false);
    }
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // 採用する初期 src を決定: online なら src 優先、offline なら thumb 優先 (旧 L52-55 同 pattern)
  function pickInitial(online: boolean): string | null | undefined {
    return online ? (src ?? thumbSrc) : (thumbSrc ?? src);
  }
  function isUsingThumbInitial(online: boolean): boolean {
    return online ? !src : !!thumbSrc;
  }

  const [currentSrc, setCurrentSrc] = useState<string | null | undefined>(() =>
    pickInitial(isOnline),
  );
  const [usingThumb, setUsingThumb] = useState<boolean>(() => isUsingThumbInitial(isOnline));
  const [bothFailed, setBothFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setCurrentSrc(pickInitial(isOnline));
    setUsingThumb(isUsingThumbInitial(isOnline));
    setBothFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, thumbSrc, isOnline]);

  function handleError() {
    if (!usingThumb && thumbSrc && thumbSrc !== currentSrc) {
      setCurrentSrc(thumbSrc);
      setUsingThumb(true);
      return;
    }
    // 既に thumb を使っていて (または thumb が無い) 失敗 → 完全失敗
    setBothFailed(true);
    onError?.();
  }

  // 両 src が無い、または両方失敗 → placeholder
  if (!currentSrc || bothFailed) {
    if (!src && !thumbSrc) return null;
    return (
      <span
        role="img"
        aria-label={alt || '写真の読込に失敗しました'}
        title="写真の読込に失敗しました"
        className={`inline-flex items-center justify-center bg-slate-200 text-slate-500 rounded border border-slate-300 ${className}`}
        data-testid="photo-placeholder"
      >
        <span className="text-xl" aria-hidden="true">
          📷
        </span>
      </span>
    );
  }

  // docs/27: 360度写真は原寸をパノラマビューアで表示 (全画面ボタン内蔵)。サムネ fallback 中は
  // 見回しに耐えないため平面表示に落とし、案内を添える。
  if (projection === 'equirectangular' && !usingThumb) {
    return (
      <PanoramaViewer
        src={currentSrc}
        baseHeadingDeg={baseHeadingDeg ?? 0}
        className={panoramaClassName}
        onError={handleError}
        alt={alt || '360度写真'}
        data-testid="photo-panorama"
      />
    );
  }

  return (
    <>
      <img
        src={currentSrc}
        alt={alt}
        className={`${className} ${enableExpand ? 'cursor-zoom-in' : ''}`.trim()}
        loading={loading}
        onError={handleError}
        onClick={enableExpand ? () => setExpanded(true) : undefined}
        data-testid="photo-img"
      />
      {projection === 'equirectangular' && usingThumb && (
        <p className="text-[11px] text-slate-500 mt-1">
          360度写真です。オンラインで原寸を取得すると見回し表示になります。
        </p>
      )}
      {expanded && (
        <PhotoLightbox
          src={currentSrc}
          thumbSrc={thumbSrc ?? null}
          alt={alt}
          onClose={() => setExpanded(false)}
        />
      )}
    </>
  );
}

/**
 * 全画面拡大モーダル (旧 L104-178 踏襲)。
 *   - 黒背景、中央に画像 (object-contain)
 *   - タップで閉じる (画像自体のタップは無効化、stopPropagation)
 *   - ピンチズームはブラウザ default 挙動に委ねる (touch-action: pinch-zoom)
 *   - body スクロール抑止 (モーダル表示中)
 */
function PhotoLightbox({
  src,
  thumbSrc,
  alt,
  onClose,
}: {
  src: string;
  thumbSrc: string | null;
  alt: string;
  onClose: () => void;
}) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [usingThumb, setUsingThumb] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // モーダル表示中は body スクロール抑止 (旧 L125-132 踏襲)
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  function handleError() {
    if (!usingThumb && thumbSrc && thumbSrc !== currentSrc) {
      setCurrentSrc(thumbSrc);
      setUsingThumb(true);
    }
  }

  return (
    <div
      ref={dialogRef}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="写真を拡大表示"
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100]"
      style={{ touchAction: 'pinch-zoom' }}
      data-testid="photo-lightbox"
    >
      <button
        type="button"
        aria-label="閉じる"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 text-white text-3xl bg-black/40 rounded-full w-10 h-10 flex items-center justify-center border-none cursor-pointer"
      >
        ×
      </button>
      <img
        src={currentSrc}
        alt={alt}
        onError={handleError}
        onClick={(e) => e.stopPropagation()}
        className="max-w-screen max-h-screen object-contain"
        style={{
          maxWidth: '100vw',
          maxHeight: '100vh',
          touchAction: 'pinch-zoom',
        }}
      />
      {usingThumb && (
        <p className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-xs">
          原寸が取得できなかったためサムネイルを拡大表示しています
        </p>
      )}
    </div>
  );
}
